import { useMemo, useState } from 'react'
import { PlaneLanding, PlaneTakeoff, Check, Plus, RefreshCw, Search, DownloadCloud } from 'lucide-react'
import { format } from 'date-fns'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { useImportFlightAwareFlight, useImportFlightAwareFlights } from '@/hooks/useFlightAware'
import type { FlightAwareSyncResult, FlightAwareFlight, FlightAwareMatch } from '@/lib/api/flightaware'

interface FlightAwareSyncDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  locationId: string
  result: FlightAwareSyncResult | null
}

type DirectionFilter = 'all' | 'inbound' | 'outbound'

const timeOf = (iso: string | null): string => (iso ? format(new Date(iso), 'MMM d HH:mm') : '—')

// Prefer "City (CODE)" when AeroAPI gives us a city/name, fall back to the code.
const airportLabel = (code: string | null, city?: string | null, name?: string | null): string => {
  const place = city ?? name
  if (place && code) return `${place} (${code})`
  return place ?? code ?? '—'
}

const matchesSearch = (q: string, ...fields: (string | null | undefined)[]): boolean => {
  if (!q) return true
  const needle = q.toLowerCase()
  return fields.some((f) => f?.toLowerCase().includes(needle))
}

export function FlightAwareSyncDrawer({ open, onOpenChange, locationId, result }: FlightAwareSyncDrawerProps) {
  const importFlight = useImportFlightAwareFlight()
  const importAll = useImportFlightAwareFlights()
  const [importedIds, setImportedIds] = useState<Set<string>>(new Set())
  const [importingId, setImportingId] = useState<string | null>(null)

  const [search, setSearch] = useState('')
  const [direction, setDirection] = useState<DirectionFilter>('all')

  const markImported = (ids: string[]) =>
    setImportedIds((prev) => {
      const next = new Set(prev)
      ids.forEach((id) => next.add(id))
      return next
    })

  const newFlights = useMemo<FlightAwareFlight[]>(() => {
    const list = result?.newAtAirport ?? []
    return list.filter(
      (f) =>
        (direction === 'all' || f.direction === direction) &&
        matchesSearch(
          search,
          f.registration,
          f.ident,
          f.aircraftType,
          f.originAirport,
          f.destinationAirport,
          f.originCity,
          f.destinationCity,
        ),
    )
  }, [result, search, direction])

  const matched = useMemo<FlightAwareMatch[]>(() => {
    const list = result?.matched ?? []
    return list.filter(
      (m) => (direction === 'all' || m.direction === direction) && matchesSearch(search, m.tail, m.ident),
    )
  }, [result, search, direction])

  // Importable + not-yet-imported within the current filter — fed to "Import all".
  const pendingImport = newFlights.filter((f) => f.importable && !importedIds.has(f.faFlightId))

  const handleImport = (flight: FlightAwareFlight) => {
    setImportingId(flight.faFlightId)
    importFlight.mutate(
      { locationId, flight },
      {
        onSuccess: () => markImported([flight.faFlightId]),
        onSettled: () => setImportingId(null),
      },
    )
  }

  const handleImportAll = () => {
    if (pendingImport.length === 0) return
    importAll.mutate(
      { locationId, flights: pendingImport },
      { onSuccess: () => markImported(pendingImport.map((f) => f.faFlightId)) },
    )
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md flex flex-col p-0 gap-0">
        <SheetHeader className="border-b">
          <div className="flex items-start justify-between gap-2 pr-6">
            <div className="min-w-0">
              <SheetTitle>FlightAware · {result?.airportIcao ?? ''}</SheetTitle>
              <SheetDescription>
                {result
                  ? `Synced ${format(new Date(result.fetchedAt), 'HH:mm')} · next ${result.horizonHours}h · ${result.counts.matched} updated, ${result.counts.newAtAirport} new`
                  : 'No results yet.'}
              </SheetDescription>
            </div>
            <Button
              size="sm"
              variant="default"
              className="shrink-0"
              disabled={pendingImport.length === 0 || importAll.isPending}
              onClick={handleImportAll}
            >
              {importAll.isPending ? (
                <RefreshCw className="h-4 w-4 mr-1.5 animate-spin" />
              ) : (
                <DownloadCloud className="h-4 w-4 mr-1.5" />
              )}
              Import all{pendingImport.length > 0 ? ` (${pendingImport.length})` : ''}
            </Button>
          </div>
        </SheetHeader>

        {/* Filters */}
        <div className="flex items-center gap-2 px-4 py-2.5 border-b">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search tail, type, airport…"
              className="h-8 pl-8 text-sm"
            />
          </div>
          <div className="flex rounded-md border p-0.5">
            {(['all', 'inbound', 'outbound'] as DirectionFilter[]).map((d) => (
              <button
                key={d}
                type="button"
                onClick={() => setDirection(d)}
                className={cn(
                  'px-2 py-1 text-xs rounded-sm capitalize transition-colors',
                  direction === d ? 'bg-secondary text-secondary-foreground font-medium' : 'text-muted-foreground hover:text-foreground',
                )}
              >
                {d === 'all' ? 'All' : d === 'inbound' ? 'Arr' : 'Dep'}
              </button>
            ))}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-6">
          {/* Matched / enriched */}
          {matched.length > 0 && (
            <section>
              <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                Matched · ETA updated
              </h4>
              <ul className="space-y-1.5">
                {matched.map((m) => (
                  <li key={m.scheduleId} className="flex items-center gap-2 rounded-md border px-3 py-2 text-sm">
                    {m.direction === 'inbound' ? (
                      <PlaneLanding className="h-4 w-4 text-emerald-600 shrink-0" />
                    ) : (
                      <PlaneTakeoff className="h-4 w-4 text-sky-600 shrink-0" />
                    )}
                    <span className="font-medium">{m.tail}</span>
                    <span className="text-muted-foreground">{m.ident ?? ''}</span>
                    <span className="ml-auto flex items-center gap-1.5 text-xs">
                      <span className="text-muted-foreground line-through">{timeOf(m.previousTime)}</span>
                      <span className="font-medium">{timeOf(m.newTime)}</span>
                      <Check className="h-3.5 w-3.5 text-emerald-600" />
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* New at airport */}
          <section>
            <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
              New at airport{newFlights.length > 0 ? ` (${newFlights.length})` : ''}
            </h4>
            {newFlights.length > 0 ? (
              <ul className="space-y-1.5">
                {newFlights.map((f) => {
                  const imported = importedIds.has(f.faFlightId)
                  return (
                    <li key={f.faFlightId} className="flex items-center gap-2 rounded-md border px-3 py-2 text-sm">
                      {f.direction === 'inbound' ? (
                        <PlaneLanding className="h-4 w-4 text-emerald-600 shrink-0" />
                      ) : (
                        <PlaneTakeoff className="h-4 w-4 text-sky-600 shrink-0" />
                      )}
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className="font-medium">{f.registration ?? f.ident ?? 'Unknown'}</span>
                          {f.aircraftType && (
                            <Badge variant="secondary" className="text-[10px] px-1 py-0">{f.aircraftType}</Badge>
                          )}
                          {f.cancelled && (
                            <Badge variant="destructive" className="text-[10px] px-1 py-0">Cancelled</Badge>
                          )}
                          {f.diverted && (
                            <Badge variant="outline" className="text-[10px] px-1 py-0 border-amber-500 text-amber-600">Diverted</Badge>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground truncate">
                          {f.direction === 'inbound'
                            ? `from ${airportLabel(f.originAirport, f.originCity, f.originName)}`
                            : `to ${airportLabel(f.destinationAirport, f.destinationCity, f.destinationName)}`} · {timeOf(f.estimatedAt ?? f.scheduledAt)}
                        </div>
                        {f.route && (
                          <div className="text-[11px] text-muted-foreground/80 truncate" title={f.route}>
                            {f.route}
                          </div>
                        )}
                      </div>
                      <div className="ml-auto shrink-0">
                        {imported ? (
                          <span className="flex items-center gap-1 text-xs text-emerald-600">
                            <Check className="h-3.5 w-3.5" /> Imported
                          </span>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7"
                            disabled={!f.importable || importingId === f.faFlightId || importAll.isPending}
                            onClick={() => handleImport(f)}
                            title={f.importable ? 'Import as schedule' : 'No tail number — cannot import'}
                          >
                            {importingId === f.faFlightId ? (
                              <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <><Plus className="h-3.5 w-3.5 mr-1" />Import</>
                            )}
                          </Button>
                        )}
                      </div>
                    </li>
                  )
                })}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground">
                {result && result.newAtAirport.length > 0
                  ? 'No flights match the current filter.'
                  : 'No new flights at the airport in this window.'}
              </p>
            )}
          </section>
        </div>
      </SheetContent>
    </Sheet>
  )
}
